import React, { useState, useEffect } from 'react';
import {
  DidYouMean,
  buildDidYouMean,
  QuerySummary,
  SearchEngine,
  ResultList,
  SearchBox
} from '@coveo/headless';
import { ExtendedWindowType, headlessEngine } from '../engine';
import { labelsObj, querySummary } from '../controllers/controller';
import { SEARCH_BOX_EVENTS } from './searchbox';

export const SITE_TYPES = Object.freeze({
  searchListing: 'search-listing',
  documentListing: 'document-listing'
});

const REPLACE_PLACEHOLDERS = Object.freeze({
  queryPlaceholder: '{{query}}',
  total: '{{totalResults}}'
});

type DidYouMeanProps = {
  correctionOnly?: boolean;
  hideOnNoResults?: boolean;
  siteType?: string;
  resultController?: ResultList;
  searchBoxController?: SearchBox;
  searchType?: string;
};

const DidYouMean = ({
  correctionOnly,
  hideOnNoResults,
  siteType,
  resultController,
  searchBoxController,
  searchType
}: DidYouMeanProps) => {
  const controller: DidYouMean = buildDidYouMean(
    headlessEngine as SearchEngine
  );
  const [state, setState] = useState(controller.state);
  const [summaryState, setSummaryState] = useState(
    (querySummary as QuerySummary).state
  );

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));
    (querySummary as QuerySummary).subscribe(() =>
      setSummaryState((querySummary as QuerySummary).state)
    );
  }, []);

  useEffect(() => {
    if (
      resultController?.state.searchResponseId &&
      searchBoxController?.state.value &&
      siteType === SITE_TYPES.searchListing
    ) {
      const global = window as ExtendedWindowType;

      const resultsCount = (querySummary as QuerySummary).state.total;
      const searchTerm = state.originalQuery;
      const didYouMeanKeyword = state.queryCorrection?.correctedQuery;

      global?.dataLayer.push({
        event: 'results_viewed',
        event_category: 'results page viewed',
        event_action: 'internal search',
        event_attributes: {
          results_count: resultsCount,
          search_term: searchTerm,
          did_you_mean_keyword: didYouMeanKeyword,
          search_type: searchType
        }
      });
    }

    if (
      resultController?.state.searchResponseId &&
      searchBoxController?.state.value &&
      siteType === SITE_TYPES.documentListing
    ) {
      const global = window as ExtendedWindowType;

      global.dataLayer?.push({
        event_action: resultController.state.hasResults ? 'success' : 'failure',
        event_attributes: {
          results_count: (querySummary as QuerySummary).state.total,
          search_term: state.originalQuery,
          search_type: searchType
        },
        event_category: 'site search',
        event: 'document_library_search'
      });
    }
  }, [resultController?.state.searchResponseId]);

  if (state?.originalQuery.length === 0) {
    return <></>;
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    controller.applyCorrection();
    window.dispatchEvent(
      new CustomEvent(SEARCH_BOX_EVENTS.updateDidYouMean, {
        detail: {
          query: state?.queryCorrection?.correctedQuery
        },
        bubbles: true
      })
    );
  };

  return (
    <>
      <div
        className={`did-you-mean ${
          hideOnNoResults && summaryState.total === 0
            ? 'did-you-mean--hidden'
            : ''
        }`}
      >
        {!correctionOnly && (
          <p className="did-you-mean__search">{`${labelsObj.didYouMeanSearch
            .replace(
              REPLACE_PLACEHOLDERS.queryPlaceholder,
              state?.originalQuery
            )
            .replace(REPLACE_PLACEHOLDERS.total, summaryState?.total)}`}</p>
        )}
        {state.hasQueryCorrection && (
          <p className="did-you-mean__correction">
            <span>{labelsObj.didYouMeanCorrection}</span>{' '}
            <button className="link--inline link--arrow" onClick={handleClick}>
              <span>{state?.queryCorrection?.correctedQuery}</span>
              <p className="did-you-mean__question-mark">?</p>
            </button>
          </p>
        )}
      </div>
    </>
  );
};

export default DidYouMean;
